const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

export interface Collection {
  id: number;
  fkusercollection: string;
  collection_title: string;
  appearance?: number;
  metadata?: ({ spotify_playlist?: string } & Record<string, unknown>) | null;
  collections_order?: number;
  promoted?: number;
  created_at: string;
  updated_at: string;
  item_count?: number;
}

export interface CreateCollectionRequest {
  collection_title: string;
  appearance?: number;
  metadata?: Record<string, unknown>;
  collections_order?: number;
  promoted?: number;
}

export interface UpdateCollectionRequest {
  collection_title?: string;
  appearance?: number;
  metadata?: Record<string, unknown>;
  collections_order?: number;
  promoted?: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
  status?: number;
}

// Core API functions - no hooks, just needs a token
const apiCall = async <T>(
  token: string | null,
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  try {
    // Build headers, conditionally including Authorization
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    // Only add Authorization header if token exists
    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/api/v2${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error:
          errorData.error?.message ||
          errorData.message ||
          `HTTP ${response.status}: ${response.statusText}`,
        code: errorData.error?.code || errorData.code,
        status: response.status,
      };
    }

    const responseData = await response.json();

    return {
      success: true,
      data: responseData.data || responseData,
    };
  } catch (error) {
    return {
      success: false,
      error: (error as Error).message,
    };
  }
};

export const favoritesApi = {
  createCollection: async (
    token: string | null,
    data: CreateCollectionRequest
  ): Promise<ApiResponse<Collection>> => {
    return apiCall<Collection>(token, "/user/collections", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  getUserCollections: async (token: string | null): Promise<ApiResponse<Collection[]>> => {
    return apiCall<Collection[]>(token, "/user/collections");
  },

  getCollectionDetails: async (
    token: string | null,
    collectionId: number
  ): Promise<ApiResponse<Collection>> => {
    return apiCall<Collection>(token, `/user/collections/${collectionId}`);
  },

  updateCollection: async (
    token: string | null,
    collectionId: number,
    data: UpdateCollectionRequest
  ): Promise<ApiResponse<Collection>> => {
    return apiCall<Collection>(token, `/user/collections/${collectionId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  deleteCollection: async (
    token: string | null,
    collectionId: number
  ): Promise<ApiResponse<void>> => {
    return apiCall<void>(token, `/user/collections/${collectionId}`, {
      method: "DELETE",
    });
  },

  updateCollectionOrders: async (
    token: string | null,
    orders: Array<{ id: number; order: number }>
  ): Promise<ApiResponse<{ updated: number }>> => {
    return apiCall<{ updated: number }>(token, "/user/collections/reorder", {
      method: "PUT",
      body: JSON.stringify({ orders }),
    });
  },

  addItemToCollection: async (
    token: string | null,
    itemId: number,
    collectionId?: number
  ): Promise<ApiResponse<unknown>> => {
    return apiCall(token, `/user/favorites/items/${itemId}`, {
      method: "POST",
      body: JSON.stringify({ collectionId }),
    });
  },

  removeItemFromCollection: async (
    token: string | null,
    itemId: number,
    collectionId?: number
  ): Promise<ApiResponse<unknown>> => {
    const queryParam = collectionId ? `?collectionId=${collectionId}` : "";
    return apiCall(token, `/user/favorites/items/${itemId}${queryParam}`, {
      method: "DELETE",
    });
  },

  getCollectionItems: async (
    token: string | null,
    collectionId: number,
    limit = 20,
    offset = 0
  ): Promise<ApiResponse<unknown[]>> => {
    return apiCall(
      token,
      `/user/collections/${collectionId}/items?limit=${limit}&offset=${offset}`
    );
  },

  getPublicCollectionItems: async (
    collectionId: number,
    limit = 20,
    offset = 0
  ): Promise<ApiResponse<unknown[]>> => {
    return apiCall(
      null,
      `/public/collections/${collectionId}/items?limit=${limit}&offset=${offset}`
    );
  },

  getUserFavorites: async (token: string | null): Promise<ApiResponse<unknown[]>> => {
    return apiCall(token, "/user/favorites");
  },

  addItemsToCollection: async (
    token: string | null,
    collectionId: number,
    itemIds: number[]
  ): Promise<ApiResponse<{ added: number; skipped: number }>> => {
    return apiCall<{ added: number; skipped: number }>(
      token,
      `/user/collections/${collectionId}/items/bulk`,
      {
        method: "POST",
        body: JSON.stringify({ itemIds }),
      }
    );
  },

  getItemLikedBy: async (
    itemId: number
  ): Promise<
    ApiResponse<{
      total_users: number;
      last_user_id: string | null;
      last_username: string | null;
    }>
  > => {
    // No auth required for this endpoint
    try {
      const response = await fetch(`${API_URL}/api/v2/user/items/${itemId}/liked-by`, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error:
            errorData.error?.message ||
            errorData.message ||
            `HTTP ${response.status}: ${response.statusText}`,
          code: errorData.error?.code || errorData.code,
          status: response.status,
        };
      }

      const responseData = await response.json();

      return {
        success: true,
        data: responseData.data || responseData,
      };
    } catch (error) {
      return {
        success: false,
        error: (error as Error).message,
      };
    }
  },
};
